// src/components/Layout/MainLayout.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { authStorage } from '../../lib/storage';
import { Header } from '../Dashboard/Header';
import { LayerTags } from '../Dashboard/LayerTags';
import { Sidebar } from '../Dashboard/Sidebar';
import { dashboardService } from '../../services/dashboardService';
import { apiService } from '../../services/api';
import {
  notifyBellAlert,
  type DesktopNotificationPayload,
} from '../../services/desktopNotificationService';
import type {
  Layer,
  SideMenuProject,
  BetaInfo,
  AlertSideMenu,
  UserProfile,
  PersonData,
} from '../../types/dashboard';
import { InfoWindowProvider } from '../../contexts/InfoWindowContext';
import { ImportFromExcelProvider } from '../../contexts/ImportFromExcelContext';
import {
  AlertNotificationsProvider,
  useAlertNotifications,
} from '../../contexts/AlertNotificationsContext';
import { setSideMenuLocal } from '../utils/CorgeeHelper';
import { useLogHoursReminder } from '../../hooks/useLogHoursReminder';
import { LogHoursReminderModal } from '../Globals/LogHoursReminderModal';
import './MainLayout.css';
import { config } from '../../config/env';
import CorgeeStorageHelper from '../Globals/CorgeeStorageHelper';
import { STORAGE_KEYS } from '../../lib/constants';
import { useSocketEvent } from '../../hooks/useSocketEvent';
import {
  validateYesterdayMetrics,
  useYesterdayMetrics,
} from '../../hooks/useYesterdayMetricsValidation';

export const MainLayout: React.FC = () => (
  <AlertNotificationsProvider>
    <MainLayoutInner />
  </AlertNotificationsProvider>
);

const MainLayoutInner: React.FC = () =>
{
  const navigate = useNavigate();
  const location = useLocation();
  const { setUnreadConversationIds, registerRefreshCallback } = useAlertNotifications();
  const logHoursReminder = useLogHoursReminder();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [layers, setLayers] = useState<Layer[]>([]);
  const [projects, setProjects] = useState<SideMenuProject[]>([]);
  const [betaData, setBetaData] = useState<BetaInfo | null>(null);
  // Cache-bust token for the brand image when it is replaced under the same path
  const [brandImageVersion, setBrandImageVersion] = useState<number | undefined>(undefined);
  const [alertSideMenu, setAlertSideMenu] = useState<AlertSideMenu[]>([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showUpdateProjects, setShowUpdateProjects] = useState(false);
  const [loading, setLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [loadedPeopleData, setLoadedPeopleData] = useState<PersonData | null>(null);
  const hasLoadedRef = React.useRef(false);
  const cardsDisabled = useYesterdayMetrics();

  // Sync a body class so InfoWindow reply elements (rendered via RenderInBody
  // outside the conversation-disabled wrapper) are also blocked.
  useEffect(() =>
  {
    if (cardsDisabled)
    {
      document.body.classList.add('C2-cards-disabled');
    }
    else
    {
      document.body.classList.remove('C2-cards-disabled');
    }
    return () =>
    {
      document.body.classList.remove('C2-cards-disabled');
    };
  }, [cardsDisabled]);

  const childFunctionRef = useRef<((background?: boolean) => void) | null>(null);

  const registerLoadData = (fn: any) =>
  {
    childFunctionRef.current = fn;
  };

  useEffect(() =>
  {
    // Prevent double execution in React StrictMode
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const access_token = authStorage.getAccessToken();

    if (!access_token)
    {
      console.warn('⚠️ MainLayout: No access_token, redirecting to login');
      setShouldRedirect(true);
      return;
    }

    loadDashboardData();
  }, []);

  const loadDashboardData = async () =>
  {
    try
    {
      setLoading(true);

      // Load data in parallel
      const [
        layersData,
        betaInfo,
        alerts,
        messagesUnread,
        sideMenuProjects,
        profileData,
        peopleData,
        hasItemCreationPermission,
        sideMenuData,
        unreadAlertIds,
      ] = await Promise.all([
        dashboardService.getPersonLayers().catch(() => []),
        dashboardService.getIsBeta().catch(
          () =>
            ({
              brandName: 'Corgee',
              brandImage: '',
              isEnabledCustomBranding: false,
              isBeta: false,
              shape: '',
            }) as BetaInfo,
        ),
        dashboardService.getAlertSideMenu().catch(() => []),
        dashboardService.getMessagesUnread().catch(() => 0),
        dashboardService
          .getSideMenuProjects(parseInt(localStorage.getItem('C2_ConfigValueId') || '0'))
          .catch(() => ({ TotalCount: 0, Projects: [] })),
        dashboardService.getUserProfile().catch(() => null as UserProfile | null),
        dashboardService.getPeopleData().catch(() => null as PersonData | null),
        apiService.get<boolean>('Security/HasItemCreationPermission').catch(() => false),
        dashboardService.GetByPerson().catch(() => []),
        dashboardService.getUnreadAlertConversationIds().catch(() => new Set<number>()),
      ]);

      setLayers(layersData);
      setBetaData(betaInfo);
      setUnreadConversationIds(unreadAlertIds);
      localStorage.setItem(
        STORAGE_KEYS.IS_ENABLED_CUSTOM_BRANDING,
        String(!!betaInfo.isEnabledCustomBranding),
      );
      localStorage.setItem(STORAGE_KEYS.BRAND_NAME, betaInfo.brandName ?? '');
      localStorage.setItem(STORAGE_KEYS.BRAND_IMAGE, betaInfo.brandImage ?? '');
      localStorage.setItem(STORAGE_KEYS.BRAND_SHAPE, betaInfo.shape ?? 'circle');
      setAlertSideMenu(alerts);
      setUnreadMessages(messagesUnread);
      setProjects(sideMenuProjects.Projects);

      // Store side menu to sessionStorage
      setSideMenuLocal(sideMenuData);

      // Store item creation permission
      localStorage.setItem('C2_hasItemCreationPermission', String(!!hasItemCreationPermission));

      // Store people data in sessionStorage for permissions and profile info
      if (peopleData)
      {
        const dataToStore = {
          ...peopleData,
          PTOApprover: !!peopleData.PTOApprover,
          HRApprover: !!peopleData.HRApprover,
        };
        sessionStorage.setItem('C2_data', JSON.stringify(dataToStore));
        localStorage.setItem('C2_HRApprover', String(dataToStore.HRApprover));
        localStorage.setItem('C2_PTOApprover', String(dataToStore.PTOApprover));
        // Avoid persisting String(undefined), which later parses as NaN
        if (dataToStore.ConfigValueIdPhases)
        {
          localStorage.setItem('C2_ConfigValueIdPhases', String(dataToStore.ConfigValueIdPhases));
        }
        else
        {
          localStorage.removeItem('C2_ConfigValueIdPhases');
        }
        // Store plugin-beta flag so validateYesterdayMetrics can read it
        localStorage.setItem('C2_isSecurityGroupBeta', String(!!peopleData.isPluginBeta));
        // Store isBeta from peopleData for ME View permissions
        localStorage.setItem('C2_isBetaME', String(!!peopleData.isBeta));
        // Keep state in sync so infoWindowStorageData uses the real isAdmin (not a hardcoded value)
        setLoadedPeopleData(peopleData);
        // Persist isBeta to localStorage and notify Sidebar/AdminConsole
        // isBeta comes from the global Parameter/GetIsBeta endpoint, not from person data
        localStorage.setItem('C2_isBeta', String(!!betaInfo.isBeta));
        window.dispatchEvent(new CustomEvent('C2_isBeta_changed', { detail: !!betaInfo.isBeta }));
      }

      // Validate yesterday's metrics (non-blocking — fires in background)
      validateYesterdayMetrics();

      if (profileData)
      {
        const imageUrl = profileData.ImageName || '';
        const personName = profileData.PersonName || '';
        sessionStorage.setItem('C2_profileImage', imageUrl);
        sessionStorage.setItem('C2_personName', personName);
        if (imageUrl)
        {
          authStorage.setImageProfile(imageUrl);
        }
      }
    }
    catch (error)
    {
      console.error('Error loading dashboard data:', error);
    }
    finally
    {
      setLoading(false);
    }
  };

  // React to custom branding changes from AdminConsole tab
  useEffect(() =>
  {
    const handler = (e: Event) =>
    {
      const patch = (e as CustomEvent).detail as Partial<{
        isEnabledCustomBranding: boolean;
        brandName: string;
        brandImage: string;
        shape: string;
      }>;
      setBetaData(prev => (prev ? { ...prev, ...patch } : prev));
      if (patch.isEnabledCustomBranding !== undefined)
        localStorage.setItem(
          STORAGE_KEYS.IS_ENABLED_CUSTOM_BRANDING,
          String(patch.isEnabledCustomBranding),
        );
      if (patch.brandName !== undefined)
        localStorage.setItem(STORAGE_KEYS.BRAND_NAME, patch.brandName);
      if (patch.brandImage !== undefined)
      {
        localStorage.setItem(STORAGE_KEYS.BRAND_IMAGE, patch.brandImage);
        setBrandImageVersion(Date.now());
      }
      if (patch.shape !== undefined) localStorage.setItem(STORAGE_KEYS.BRAND_SHAPE, patch.shape);
    };
    window.addEventListener('C2_branding_changed', handler);
    return () => window.removeEventListener('C2_branding_changed', handler);
  }, []);

  // Branding changes made by an admin in another window arrive via socket;
  // re-dispatch them as C2_branding_changed so every listener stays in sync
  const dispatchBrandingChanged = useCallback((patch: Record<string, unknown>) =>
  {
    window.dispatchEvent(new CustomEvent('C2_branding_changed', { detail: patch }));
  }, []);

  const handleBrandNameSocket = useCallback(
    (data: any) =>
    {
      dispatchBrandingChanged({ brandName: data?.brandName ?? data?.BrandName ?? '' });
    },
    [dispatchBrandingChanged],
  );

  const handleImageBrandSocket = useCallback(
    (data: any) =>
    {
      dispatchBrandingChanged({
        brandImage: data?.image ?? '',
        ...(data?.shape ? { shape: data.shape } : {}),
      });
    },
    [dispatchBrandingChanged],
  );

  const handleEnableBrandSocket = useCallback(
    (data: any) =>
    {
      dispatchBrandingChanged({ isEnabledCustomBranding: !!data?.enable });
    },
    [dispatchBrandingChanged],
  );

  useSocketEvent('socket:client:updateBrandName', handleBrandNameSocket);
  useSocketEvent('socket:client:updateImageBrand', handleImageBrandSocket);
  useSocketEvent('socket:client:updateEnableBrand', handleEnableBrandSocket);

  const refreshAlertCounts = useCallback(async () =>
  {
    try
    {
      const [alerts, unreadAlertIds] = await Promise.all([
        dashboardService.getAlertSideMenu(),
        dashboardService.getUnreadAlertConversationIds(),
      ]);
      setAlertSideMenu(alerts);
      setUnreadConversationIds(unreadAlertIds);
    }
    catch (error)
    {
      console.error('Error refreshing alert counts:', error);
    }
  }, [setUnreadConversationIds]);

  // Optimistic update: decrement counter locally without API call
  const decrementAlertCount = useCallback((configValueId: number, amount: number = 1) =>
  {
    setAlertSideMenu(prev =>
      prev.map(alert =>
        alert.ConfigValueId === configValueId
          ? { ...alert, Total: Math.max(0, alert.Total - amount) }
          : alert,
      ),
    );
  }, []);

  // Register so AlertNotificationsContext can trigger a counter refresh
  useEffect(() =>
  {
    registerRefreshCallback(refreshAlertCounts);
  }, [registerRefreshCallback, refreshAlertCounts]);

  const refreshSideMenuProjects = useCallback(async () =>
  {
    try
    {
      const result = await dashboardService.getSideMenuProjects(9);
      setProjects(result.Projects);
    }
    catch (error)
    {
      console.error('Error refreshing side menu projects:', error);
    }
  }, []);

  const handleRefreshProjects = useCallback(async () =>
  {
    await refreshSideMenuProjects();
    setShowUpdateProjects(false);
  }, [refreshSideMenuProjects]);

  useSocketEvent('socket:client:UpdateProjects', () => setShowUpdateProjects(true));

  const refreshLayers = useCallback(async () =>
  {
    try
    {
      const layersData = await dashboardService.getPersonLayers();
      setLayers(layersData);
      window.dispatchEvent(
        new CustomEvent('layersToHideChanged', {
          detail: { hiddenLayers: JSON.parse(sessionStorage.getItem('C2_LayersToHide') || '[]') },
        }),
      );
    }
    catch (error)
    {
      console.error('Error refreshing layers:', error);
    }
  }, []);

  useEffect(() =>
  {
    window.addEventListener('sideMenuProjectsRefresh', refreshSideMenuProjects);
    return () => window.removeEventListener('sideMenuProjectsRefresh', refreshSideMenuProjects);
  }, [refreshSideMenuProjects]);

  useEffect(() =>
  {
    window.addEventListener('C2_layers_changed', refreshLayers);
    return () => window.removeEventListener('C2_layers_changed', refreshLayers);
  }, [refreshLayers]);

  // The API lists side menu projects most-recently-visited first: keep that order
  // locally on navigation instead of waiting for a reload.
  // Selecting a project directly from the sidebar must NOT reorder the list;
  // only navigations coming from card options (project links) do.
  useEffect(() =>
  {
    const match = location.pathname.match(/\/ProjectDetails\/(\d+)/);
    if (!match) return;
    if ((location.state as { fromSideMenu?: boolean } | null)?.fromSideMenu) return;

    const visitedProjectId = Number(match[1]);

    setProjects(prev =>
    {
      const index = prev.findIndex(project => project.ProjectID === visitedProjectId);
      if (index <= 0) return prev;

      const reordered = [...prev];
      const [visited] = reordered.splice(index, 1);
      return visited ? [visited, ...reordered] : prev;
    });
  }, [location.pathname, location.state]);

  const refreshSideMenu = useCallback(async () =>
  {
    try
    {
      const sideMenuData = await dashboardService.GetByPerson();
      setSideMenuLocal(sideMenuData);
    }
    catch (error)
    {
      console.error('Error refreshing side menu:', error);
    }
  }, []);

  const refreshPeopleData = useCallback(async () =>
  {
    try
    {
      const peopleData = await dashboardService.getPeopleData();
      if (peopleData)
      {
        const dataToStore = {
          ...peopleData,
          PTOApprover: !!peopleData.PTOApprover,
          HRApprover: !!peopleData.HRApprover,
        };
        sessionStorage.setItem('C2_data', JSON.stringify(dataToStore));
        localStorage.setItem('C2_HRApprover', String(dataToStore.HRApprover));
        localStorage.setItem('C2_PTOApprover', String(dataToStore.PTOApprover));
        localStorage.setItem('C2_isBetaME', String(!!peopleData.isBeta));
        setLoadedPeopleData(peopleData);
      }
    }
    catch (error)
    {
      console.error('Error refreshing people data:', error);
    }
  }, []);

  const handleWizardUpdates = useCallback(
    (data: any) =>
    {
      if (!data?.wizardChanges) return;
      const delay = typeof data.delay === 'number' ? data.delay : 0;
      setTimeout(() =>
      {
        if (data.Type === 'LAYER')
        {
          refreshLayers();
        }
        else if (data.Type === 'CARDTYPE')
        {
          refreshSideMenu();
        }
        else if (data.Type === 'SECGROUPS')
        {
          refreshLayers();
          refreshPeopleData();
        }
      }, delay * 8000);
    },
    [refreshLayers, refreshSideMenu, refreshPeopleData],
  );

  useSocketEvent('socket:client:WizardUpdates', handleWizardUpdates);

  const alertRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingAlertRef = useRef<DesktopNotificationPayload | null>(null);

  const refreshAlertsFromSocket = useCallback(() =>
  {
    // Cancel any pending refresh so rapid bursts collapse into one request
    if (alertRefreshTimerRef.current) clearTimeout(alertRefreshTimerRef.current);

    // 2 s settle delay + jitter: avoids stale reads and spreads bursts
    const delay = 2000 + Math.random() * 3000;
    alertRefreshTimerRef.current = setTimeout(async () =>
    {
      // One notification + sound per burst, with the last payload (legacy MenuCtrl.js:1062-1072)
      const pending = pendingAlertRef.current;
      pendingAlertRef.current = null;
      if (pending) notifyBellAlert(pending);

      try
      {
        const alerts = await dashboardService.getTotalAlertsByPerson();
        setAlertSideMenu(alerts);
      }
      catch
      {
        // silently ignore socket-triggered refresh failures
      }
    }, delay);
  }, []);

  // Bell alerts also raise the OS notification and the sound, each one gated by
  // its own setting in the profile (legacy notificationService.sendNotification)
  const handleAlertNotification = useCallback(
    (data: DesktopNotificationPayload) =>
    {
      pendingAlertRef.current = data;
      refreshAlertsFromSocket();
    },
    [refreshAlertsFromSocket],
  );

  useSocketEvent('socket:client:notification', handleAlertNotification);
  useSocketEvent('socket:client:tenantNotification', refreshAlertsFromSocket);

  const handleAddChatMessage = useCallback(() =>
  {
    setUnreadMessages(prev => prev + 1);
  }, []);

  const handleUpdateTotalMessagesUnread = (total: number) =>
  {
    setTimeout(() => setUnreadMessages(total), 500);
  };

  useSocketEvent('socket:client:addChatMessage', handleAddChatMessage);
  useSocketEvent('socket:client:updateTotalMessagesUnread', handleUpdateTotalMessagesUnread);
  useSocketEvent('socket:client:notificationProjectChannel', handleAlertNotification);

  const handleMenuToggle = useCallback(() =>
  {
    setSidebarOpen(prev => !prev);
  }, []);

  const handleProjectChange = useCallback(
    (projectId: number) =>
    {
      // fromSideMenu keeps the side menu order untouched (see reorder effect above)
      navigate(`/ProjectDetails/${projectId}`, { state: { fromSideMenu: true } });
    },
    [navigate],
  );

  const userName = authStorage.getGivenName() || authStorage.getUsername() || 'User';
  const userEmail = authStorage.getUsername() || '';

  const infoWindowApi = React.useMemo(() =>
  {
    const token = authStorage.getAccessToken() || '';
    const personId = authStorage.getPersonId() || '0';
    return {
      url: config.api.baseUrl,
      token,
      refreshToken: '',
      SocketServer: config.socket.server,
      TenancyName: config.tenant.defaultName,
      PersonId: personId,
    };
  }, []);

  const infoWindowConfig = React.useMemo(() =>
  {
    return {
      weburl: config.web.url,
      apiurl: config.api.baseUrl,
      webChaturl: config.web.url + '/CHAT',
    };
  }, []);

  const infoWindowStorageData = React.useMemo(() =>
  {
    const personid = parseInt(authStorage.getPersonId() || '0', 10);
    return {
      data: {
        isAdmin: !!loadedPeopleData?.isAdmin,
        canEdit: loadedPeopleData ? !!loadedPeopleData.canEdit : true,
        canDelete: loadedPeopleData ? !!loadedPeopleData.canDelete : true,
      },
      allLayers: CorgeeStorageHelper.getAllLayers(),
      layersToHide: CorgeeStorageHelper.getLayersToHide(),
      personid,
      enableChat: true,
    };
  }, [loadedPeopleData]);

  const infoWindowUserData = React.useMemo(
    () => ({
      imageProfile: 'Images/user.png',
      personName: userName,
    }),
    [userName],
  );

  const infoWindowRealItemTypes = React.useMemo(
    () => ({
      Users: { Name: 'People', Icon: 'icons-menu-account-box' },
      Tasks: { Name: 'Tasks', Icon: 'icons-menu-epic' },
    }),
    [],
  );

  if (shouldRedirect)
  {
    return <Navigate to="/" replace />;
  }

  if (loading)
  {
    return (
      <div className="main-layout__loading">
        <div className="main-layout__loading-inner">
          <div className="main-layout__spinner" />
          <p className="main-layout__loading-text">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <InfoWindowProvider
      api={infoWindowApi}
      config={infoWindowConfig}
      storageData={infoWindowStorageData}
      userData={infoWindowUserData}
      realItemTypes={infoWindowRealItemTypes}
      functionsLoad={() =>
      {
        childFunctionRef.current?.(true);
      }}
    >
      <ImportFromExcelProvider>
        <div className="main-layout__root">
          {/* Layer Tags - Always visible */}
          <LayerTags layers={layers} />

          {/* Header - Always visible */}
          <Header
            user={{ name: userName, email: userEmail }}
            onMenuToggle={handleMenuToggle}
            unreadMessages={unreadMessages}
            betaData={betaData}
            brandImageVersion={brandImageVersion}
            alertSideMenu={alertSideMenu}
            onRefreshAlerts={refreshAlertCounts}
            onDecrementAlertCount={decrementAlertCount}
          />

          {/* Main Content Area */}
          <div className="main-layout__body">
            {/* Sidebar - Always visible */}
            <Sidebar
              isOpen={sidebarOpen}
              projects={projects}
              onProjectChange={handleProjectChange}
              onRefreshAlerts={refreshAlertCounts}
              alertSideMenu={alertSideMenu}
              showUpdateProjects={showUpdateProjects}
              onRefreshProjects={handleRefreshProjects}
              onDecrementAlertCount={decrementAlertCount}
            />

            {/* Content Area - Changes based on route */}
            <main className="main-layout__content">
              <Outlet context={{ registerLoadData }} />
            </main>
          </div>

          {logHoursReminder.isOpen && (
            <LogHoursReminderModal
              personName={logHoursReminder.personName}
              onClose={logHoursReminder.onClose}
            />
          )}
        </div>
      </ImportFromExcelProvider>
    </InfoWindowProvider>
  );
};
